import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useGetCountriesQuery} from "store/services/authApi";
import {
    useGetMarketplaceCategoriesQuery,
    useGetMarketplaceListingsQuery,
    useGetMarketplaceSuppliersQuery,
} from "../../store/services/publicApi";
import {filterSearchOptions, marketplaceSearchQuery} from "./searchQuery";

type SearchOption = {id: string; label: string};

type SearchFieldProps = {
    icon: string;
    label: string;
    placeholder: string;
    grow: number;
    value: string;
    onChange: (value: string) => void;
    options: SearchOption[];
    onSelect?: (option: SearchOption) => void;
    emptyText: string;
};

const SearchField = ({
    icon,
    label,
    placeholder,
    grow,
    value,
    onChange,
    options,
    onSelect,
    emptyText,
}: SearchFieldProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLLabelElement>(null);
    const matches = filterSearchOptions(options, value);

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    return (
        <label ref={rootRef} className={`oec-search-field${open ? " is-open" : ""}`} style={{flexGrow: grow}}>
            <i className={`${icon} oec-search-field-icon`}/>
            <span className="oec-search-field-text">
                <span className="oec-search-field-label">{label}</span>
                <input
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    autoComplete="off"
                    onFocus={() => setOpen(true)}
                    onChange={(event) => {
                        onChange(event.target.value);
                        setOpen(true);
                    }}
                />
            </span>
            <button
                type="button"
                className="oec-search-field-chevron-btn"
                aria-label={`Show ${label} options`}
                onClick={() => setOpen((current) => !current)}
            >
                <i className={`fas fa-chevron-down oec-search-field-chevron${open ? " is-open" : ""}`}/>
            </button>
            {open && (
                <ul className="oec-search-menu" role="listbox">
                    {value && (
                        <li>
                            <button
                                type="button"
                                className="oec-search-option"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    onChange("");
                                    onSelect?.({id: "", label: ""});
                                    setOpen(false);
                                }}
                            >
                                Clear
                            </button>
                        </li>
                    )}
                    {matches.length === 0 && (
                        <li className="oec-search-option oec-search-option-empty">{emptyText}</li>
                    )}
                    {matches.map((option) => (
                        <li key={option.id}>
                            <button
                                type="button"
                                className="oec-search-option"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    onChange(option.label);
                                    onSelect?.(option);
                                    setOpen(false);
                                }}
                            >
                                {option.label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </label>
    );
};

export const MarketplaceSearch = () => {
    const navigate = useNavigate();
    const [product, setProduct] = useState("");
    const [supplier, setSupplier] = useState("");
    const [country, setCountry] = useState("");
    const [market, setMarket] = useState("");
    const [categoryId, setCategoryId] = useState("");

    const {data: listings} = useGetMarketplaceListingsQuery({});
    const {data: suppliers} = useGetMarketplaceSuppliersQuery(undefined);
    const {data: countries} = useGetCountriesQuery(undefined);
    const {data: categories} = useGetMarketplaceCategoriesQuery({});

    const productOptions = useMemo<SearchOption[]>(
        () => (listings || []).map((listing: {id: string; name: string}) => ({id: listing.id, label: listing.name})),
        [listings],
    );
    const supplierOptions = useMemo<SearchOption[]>(
        () => (suppliers || []).map((item: {id: string; name: string}) => ({id: item.id, label: item.name})),
        [suppliers],
    );
    const countryOptions = useMemo<SearchOption[]>(
        () => (countries || []).map((item: {id: string; name: string}) => ({id: item.id, label: item.name})),
        [countries],
    );
    const marketOptions = useMemo<SearchOption[]>(
        () => (categories || []).map((item: {id: string; name: string}) => ({id: item.id, label: item.name})),
        [categories],
    );

    const pickMarket = (option: SearchOption) => {
        setMarket(option.label);
        setCategoryId(option.id);
    };

    return (
        <section className="oec-search-section">
            <div className="oec-section-inner">
                <div className="oec-search-panel">
                    <p className="oec-search-title">Discover Energy Products &amp; Suppliers</p>
                    <div className="oec-search-row">
                        <SearchField
                            icon="fas fa-search"
                            label="Product / Solution"
                            placeholder="e.g. Solar panels, batteries"
                            grow={203}
                            value={product}
                            onChange={setProduct}
                            options={productOptions}
                            emptyText="No matching products"
                        />
                        <SearchField
                            icon="fas fa-filter"
                            label="Supplier"
                            placeholder="Search supplier name"
                            grow={181}
                            value={supplier}
                            onChange={setSupplier}
                            options={supplierOptions}
                            emptyText="No matching suppliers"
                        />
                        <SearchField
                            icon="fas fa-map-marker-alt"
                            label="Country"
                            placeholder="Select country"
                            grow={181}
                            value={country}
                            onChange={setCountry}
                            options={countryOptions}
                            emptyText="No matching countries"
                        />
                        <SearchField
                            icon="fas fa-store"
                            label="Market"
                            placeholder="Select market"
                            grow={177}
                            value={market}
                            onChange={(value) => {
                                setMarket(value);
                                const match = marketOptions.find((option) => option.label.toLowerCase() === value.trim().toLowerCase());
                                setCategoryId(match?.id || "");
                            }}
                            onSelect={pickMarket}
                            options={marketOptions}
                            emptyText="No matching markets"
                        />
                        <button
                            className="oec-search-btn"
                            onClick={() => {
                                const query = marketplaceSearchQuery({product, supplier, country, categoryId});
                                navigate(query ? `/marketplace?${query}` : "/marketplace");
                            }}
                        >
                            Search <i className="fas fa-arrow-right"/>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};
